import {ActionStatus, ActionStatusType} from '@domain/pom'
import {MenuItem, TextField} from '@mui/material'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'

/** Statuses the user can manually select (no approval workflow) */
const MANUAL_STATUSES: ActionStatusType[] = [
  ActionStatus.Pending,
  ActionStatus.Ongoing,
  ActionStatus.Done
]

/** Statuses the user can select after the action has been approved */
const POST_APPROVAL_STATUSES: ActionStatusType[] = [ActionStatus.Ongoing, ActionStatus.Done]

type Props = {
  onChange: (data: ActionStatusType) => void
  value?: ActionStatusType
  error?: boolean
  needsApproval?: boolean
}

export const StatusField: React.FC<Props> = ({
  onChange,
  error,
  value = 'pending',
  needsApproval = false
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  const availableStatuses = useMemo(() => {
    if (
      needsApproval &&
      (value === ActionStatus.Approved || POST_APPROVAL_STATUSES.includes(value))
    ) {
      return POST_APPROVAL_STATUSES
    }
    if (needsApproval && value && !MANUAL_STATUSES.includes(value)) {
      return [value, ...MANUAL_STATUSES]
    }
    return MANUAL_STATUSES
  }, [needsApproval, value])

  const isDisabled =
    needsApproval &&
    value !== ActionStatus.Approved &&
    value !== ActionStatus.Ongoing &&
    value !== ActionStatus.Done

  return (
    <TextField
      fullWidth
      variant="filled"
      label={t('actionDialog.status')}
      select
      data-test-id="action-status"
      value={value}
      error={error}
      disabled={isDisabled}
      onChange={(event) => {
        onChange(event.target.value as ActionStatusType)
      }}
      SelectProps={{
        renderValue: (selected) => t(`actions.${selected}`)
      }}
    >
      {availableStatuses.map((status) => (
        <MenuItem
          data-test-id={`action-status-dropdown-item-${status}`}
          key={status}
          value={status}
        >
          {t(`actions.${status}`)}
        </MenuItem>
      ))}
    </TextField>
  )
}
